/* Pro-only canvas engine. Sticky canvas, scroll/frame mapping, viewport gating and
   DPR-aware drawing; caption motion comes from 1b. No shared selectors, no
   perpetual animation loop, no global page changes.

   Frame delivery (rewritten 9 Sep 2026 for smoothness): every frame of a sequence
   is preloaded as a plain <img> that the browser keeps in its own memory cache, so
   the scroll handler never waits on a fetch or a decode. Frames arrive coarse to
   fine (every 8th, then every 4th, 2nd, all) nearest to the current position first,
   so any scroll position has a nearby frame after the first few hundred KB and the
   motion refines as the rest lands. The previous design fetched frames on demand
   with a ±3 decode window, which could never keep up with a real scroll. */
(function () {
'use strict';
const MOTION=[[0.683,0.485],[0.68,0.484],[0.672,0.483],[0.663,0.482],[0.657,0.481],[0.654,0.481],[0.652,0.481],[0.655,0.481],[0.655,0.481],[0.658,0.48],[0.663,0.48],[0.665,0.479],[0.668,0.479],[0.668,0.478],[0.671,0.479],[0.671,0.48],[0.666,0.48],[0.651,0.489],[0.635,0.497],[0.618,0.508],[0.591,0.522],[0.56,0.54],[0.523,0.558],[0.488,0.58],[0.483,0.601],[0.474,0.623],[0.468,0.645],[0.456,0.666],[0.447,0.686],[0.448,0.7],[0.445,0.712],[0.464,0.67],[0.488,0.577],[0.526,0.492],[0.609,0.47],[0.638,0.481],[0.569,0.483],[0.496,0.484],[0.467,0.508],[0.447,0.533],[0.432,0.553],[0.413,0.573],[0.394,0.595],[0.381,0.615],[0.379,0.636],[0.385,0.658],[0.399,0.679],[0.412,0.698],[0.422,0.713],[0.43,0.726],[0.433,0.734],[0.435,0.741],[0.438,0.745],[0.439,0.748],[0.44,0.749],[0.44,0.749],[0.44,0.749],[0.44,0.75],[0.438,0.75],[0.439,0.76],[0.443,0.772],[0.444,0.782],[0.445,0.774],[0.442,0.767],[0.441,0.759],[0.449,0.783],[0.458,0.799],[0.455,0.805],[0.457,0.806],[0.469,0.803],[0.48,0.794],[0.483,0.781],[0.527,0.764],[0.576,0.75],[0.583,0.748],[0.582,0.751],[0.578,0.757],[0.575,0.764],[0.573,0.767],[0.566,0.767],[0.561,0.767],[0.558,0.767],[0.554,0.766],[0.556,0.769],[0.56,0.773],[0.559,0.705],[0.558,0.568],[0.583,0.484],[0.594,0.477],[0.565,0.489],[0.506,0.496],[0.471,0.5],[0.496,0.498],[0.529,0.495],[0.564,0.499],[0.587,0.508],[0.593,0.52],[0.594,0.533],[0.594,0.549],[0.593,0.566],[0.592,0.581],[0.587,0.596],[0.582,0.606],[0.574,0.612],[0.568,0.616],[0.564,0.619],[0.562,0.619],[0.559,0.621],[0.557,0.623],[0.557,0.623],[0.556,0.623],[0.544,0.623],[0.515,0.622],[0.497,0.618],[0.487,0.616],[0.485,0.615],[0.486,0.614],[0.487,0.612],[0.486,0.612],[0.486,0.612],[0.487,0.611],[0.487,0.61],[0.487,0.608],[0.487,0.606],[0.486,0.604],[0.487,0.608],[0.492,0.62],[0.505,0.635],[0.514,0.649],[0.516,0.658],[0.495,0.662],[0.449,0.654],[0.392,0.639],[0.364,0.606],[0.346,0.569],[0.34,0.534],[0.33,0.513],[0.318,0.503],[0.31,0.498],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497],[0.308,0.497]];
const LOGO_SVG='<svg viewBox="0 0 794.22 769.95" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><polygon points="794.22 0 794.22 130.99 617.4 207.33 405.89 298.65 0 149.32 0 0 397.11 149.32 794.22 0"/><path d="M767.54,636.76V139.79l-90.64,39.27v88.09c0,19.2-11.16,36.66-28.59,44.72l-121.56,56.2v401.88l240.79-133.2Z"/><path d="M24.62,636.76V139.79s90.64,39.27,90.64,39.27v88.09c0,19.2,11.16,36.66,28.59,44.72l121.56,56.2v401.88S24.62,636.76,24.62,636.76Z"/></svg>';
const clamp = (v,a=0,b=1) => Math.max(a,Math.min(b,v));
const seg=(f,a,b)=>b===a?(f>=b?1:0):clamp((f-a)/(b-a));
const out=t=>1-Math.pow(1-t,3);
const ease=t=>t<.5?4*t*t*t:1-Math.pow(-2*t+2,3)/2;

/* Progressive, bounded-concurrency preloader for one image sequence. Frames are
   ordinary <img> elements; we never re-fetch or evict them ourselves. Frames near
   the current index are decoded ahead of time (img.decode) so drawImage does not
   block on a decode. `circular` treats the sequence as a loop (360 turntable). */
const TOTAL_LIMIT=24;let totalInflight=0;
class FrameSet{
  constructor(urls,options={}){
    this.urls=urls;this.n=urls.length;this.images=new Array(this.n).fill(null);this.pending=new Set(urls.map((_,i)=>i));
    this.inflight=new Map();this.failed=new Map();this.decoded=new Set();this.decoding=new Set();
    this.limit=options.limit||6;this.near=options.near??12;this.ahead=options.ahead??6;this.circular=Boolean(options.circular);
    this.onLoad=options.onLoad||(()=>{});this.index=0;this.direction=1;this.active=false;this.loadedCount=0;this.destroyed=false;
    // Until the page's load event has fired, only the coarse level and the frames next
    // to the current position are fetched, so the preload never holds `load` open.
    this.gate=options.gate||(()=>2);
  }
  get complete(){return this.loadedCount>=this.n;}
  level(i){return i%8===0?0:i%4===0?1:i%2===0?2:3;}
  distance(i){
    let d=i-this.index;if(this.circular){const half=this.n/2;if(d>half)d-=this.n;else if(d<-half)d+=this.n;}
    // Frames ahead in the scroll direction matter slightly more than frames behind.
    return (d*this.direction>=0?1:1.5)*Math.abs(d);
  }
  seek(index,direction){
    this.index=clamp(index,0,this.n-1);if(direction)this.direction=direction<0?-1:1;this.active=true;this.pump();this.decodeNear();
  }
  pause(){this.active=false;}
  adopt(i,image){
    if(this.destroyed||i<0||i>=this.n||this.images[i]||!image||!image.complete||!image.naturalWidth)return;
    this.pending.delete(i);this.images[i]=image;this.loadedCount++;
  }
  next(){
    let best=-1,bestKey=Infinity;const now=Date.now(),phase=Number(this.gate());
    for(const i of this.pending){
      if(this.inflight.has(i))continue;const failedAt=this.failed.get(i);if(failedAt&&now-failedAt<5000)continue;
      const d=this.distance(i);if(phase<2&&d>this.near&&i%(phase<1?16:8)!==0)continue;
      const key=(d<=this.near?0:this.level(i)+1)*1e5+d;
      if(key<bestKey){bestKey=key;best=i;}
    }
    return best;
  }
  pump(){
    if(!this.active||this.destroyed)return;
    // Before the page's load event, leave most of the connection to the page itself.
    const limit=Number(this.gate())>0?this.limit:Math.min(3,this.limit);
    while(this.inflight.size<limit&&totalInflight<TOTAL_LIMIT){
      const i=this.next();if(i<0)break;
      const image=new Image();image.decoding='async';let done=false;totalInflight++;
      const finish=ok=>{
        if(done)return;done=true;totalInflight--;clearTimeout(timer);image.onload=image.onerror=null;this.inflight.delete(i);
        if(this.destroyed)return;
        if(ok&&image.naturalWidth){this.pending.delete(i);this.images[i]=image;this.loadedCount++;this.onLoad(i);this.decodeNear();}
        else{this.failed.set(i,Date.now());image.src='';}
        this.pump();
      };
      const timer=setTimeout(()=>finish(false),20000);
      image.onload=()=>finish(true);image.onerror=()=>finish(false);
      this.inflight.set(i,{cancel:()=>finish(false)});image.src=this.urls[i];
    }
  }
  decodeNear(){
    if(this.destroyed||typeof HTMLImageElement.prototype.decode!=='function')return;
    const order=[0];for(let d=1;d<=this.ahead;d++)order.push(d*this.direction);order.push(-this.direction,-2*this.direction,-3*this.direction);
    for(const offset of order){
      if(this.decoding.size>=4)return;
      let i=this.index+offset;if(this.circular)i=((i%this.n)+this.n)%this.n;if(i<0||i>=this.n)continue;
      const image=this.images[i];if(!image||this.decoded.has(i)||this.decoding.has(i))continue;
      this.decoding.add(i);image.decode().then(()=>{this.decoded.add(i);},()=>{}).then(()=>{this.decoding.delete(i);if(!this.destroyed)this.decodeNear();});
    }
  }
  nearest(index){
    if(index<0||index>=this.n)index=clamp(index,0,this.n-1);
    if(this.images[index])return {image:this.images[index],index};
    const span=this.circular?Math.ceil(this.n/2):this.n;
    for(let d=1;d<=span;d++){
      let a=index+d,b=index-d;
      if(this.circular){a%=this.n;b=((b%this.n)+this.n)%this.n;}
      if(a<this.n&&this.images[a])return {image:this.images[a],index:a};
      if(b>=0&&this.images[b])return {image:this.images[b],index:b};
    }
    return null;
  }
  destroy(){this.destroyed=true;this.active=false;for(const task of this.inflight.values())task.cancel();this.inflight.clear();this.images.fill(null);this.pending.clear();this.decoded.clear();this.decoding.clear();}
}

class IAPStory {
  constructor(owner){
    this.owner=owner;this.cfg=owner.config;this.root=owner.querySelector('[data-iap-story]');this.stage=owner.querySelector('[data-iap-stage]');this.canvas=owner.querySelector('[data-iap-story-canvas]');this.ctx=this.canvas.getContext('2d');this.poster=owner.querySelector('[data-iap-story-poster]');this.loadingBar=owner.querySelector('[data-iap-loading]');
    this.start=Number(this.cfg.storyStart)||0;this.end=Math.max(this.start+1,Number(this.cfg.storyEnd)||670);
    // Every 2nd source frame at full quality (280 desktop / 165 mobile), eased by smoothing.
    this.step=Math.max(1,Number(this.cfg.optimizedStoryStep)||2);this.frame=this.start;this.frames=[];
    for(let n=this.start;n<=this.end;n+=this.step)this.frames.push(n);
    this.set=null;this.index=0;this.shown=0;this.lastDraw=0;
    // Time constant of the follow-through easing (ms); 0 = draw the exact scroll frame.
    this.tau={off:0,light:60,medium:120}[this.cfg.storySmoothing]??60;this.blend=this.cfg.storyBlend!==false;this.buildCaptions();
    this.observer=new IntersectionObserver(entries=>{this.near=entries.some(e=>e.isIntersecting);if(this.near)this.update();else if(this.set)this.set.pause();},{rootMargin:'4000px'});this.observer.observe(this.root);
  }
  motion(f){return MOTION[clamp(Math.round((f-this.start)/(this.end-this.start)*(MOTION.length-1)),0,MOTION.length-1)];}
  buildCaptions(){
    const host=this.owner.querySelector('[data-iap-captions]');host.replaceChildren();
    this.caps=this.cfg.captions.map(data=>{
      const c={...data};const el=document.createElement('div');el.className='iap-cap iap-cap-'+c.kind;el.style.setProperty('--iap-cap-top',c.top+'%');el.dataset.side=c.side||'left';c.el=el;
      const h=document.createElement('h3');c.h=h;
      if(c.kind==='title'){
        const mark=document.createElement('span');mark.className='iap-mark';mark.innerHTML=LOGO_SVG;el.append(mark);c.mark=mark;c.letters=[];
        c.text.split(' ').forEach((word,i)=>{if(i)h.append(' ');const box=document.createElement('span');box.className='iap-word';for(const char of word){const s=document.createElement('span');s.className='iap-letter';s.textContent=char;box.append(s);c.letters.push(s);}h.append(box);});
      }else if(c.kind==='say'){
        c.words=c.text.split(' ').map((word,i)=>{if(i)h.append(' ');const box=document.createElement('span');box.className='iap-word-mask';const inner=document.createElement('i');inner.textContent=word;box.append(inner);h.append(box);return inner;});
      }else{
        const lead=document.createElement('div');lead.className='iap-cap-lead';c.dot=document.createElement('span');c.dot.className='iap-cap-dot';c.line=document.createElement('span');c.line.className='iap-cap-line';lead.append(c.dot,c.line);el.append(lead);h.textContent=c.text;
      }
      el.append(h);if(c.kind==='say'){c.bar=document.createElement('div');c.bar.className='iap-cap-bar';el.append(c.bar);}host.append(el);return c;
    });
  }
  rebuild(){
    if(this.set)this.set.destroy();
    this.renderedIndex=null;this.renderedKey=null;this.requestedIndex=null;this.ready=false;this.lastDraw=0;this.frameW=0;this.frameH=0;this.canvas.classList.remove('iap-ready');if(this.poster)this.poster.hidden=false;
    if(this.static||!this.mapping){this.set=null;return;}
    const set=new FrameSet(this.frames.map(n=>this.owner.sequence(this.mapping,'story',n)),{limit:12,near:16,ahead:14,gate:()=>this.owner.storyPhase?this.owner.storyPhase():2,onLoad:i=>{
      if(set!==this.set||!this.owner.isConnected)return;
      if(this.loadingBar)this.loadingBar.style.width=set.complete?'0':(set.loadedCount/set.n*100)+'%';
      if(set.complete&&typeof this.owner.warmViewers==='function')this.owner.warmViewers();
      // Redraw when a closer frame than the one on screen has arrived.
      const current=this.renderedIndex==null?Infinity:Math.abs(this.renderedIndex-this.index);
      if(Math.abs(i-this.index)<=current)this.scheduleDraw();
    }});
    this.set=set;
  }
  select(mapping){
    this.mapping=mapping;this.static=this.owner.reduced||!this.owner.sequence(mapping,'story',this.start);this.owner.classList.toggle('iap-static',this.static);
    this.rebuild();this.resize();this.update();
  }
  resize(){
    const portrait=window.innerWidth<820;
    if(this.portrait!==portrait){
      this.portrait=portrait;this.owner.classList.toggle('iap-portrait-story',portrait);this.end=Math.max(this.start+1,Number(portrait?this.cfg.mobileStoryEnd:this.cfg.storyEnd));this.frames=[];
      for(let n=this.start;n<=this.end;n+=this.step)this.frames.push(n);
      if(this.mapping)this.rebuild();
    }
    this.narrow=window.innerWidth<820;this.height=Math.max(300,window.innerHeight-this.owner.headerHeight);
    const rootHeight=this.static?this.height:this.height*((this.narrow?this.cfg.mobileLength:this.cfg.desktopLength)+1);
    if(this.rootHeight!==rootHeight){this.rootHeight=rootHeight;this.root.style.height=rootHeight+'px';}
    const rect=this.stage.getBoundingClientRect();const w=rect.width,h=rect.height;
    if(w!==this.w||h!==this.h){this.w=w;this.h=h;this.band();}
    this.renderedKey=null;this.draw();
  }
  // The canvas backing store is exactly one frame (set on first draw) and CSS object-fit
  // scales it: drawImage(frame,0,0) is the whole per-frame cost.
  // Captions position themselves against the drawn image band, computed here from the
  // same fit rules the stylesheet applies (cover on desktop, contain in portrait).
  band(){
    if(!this.frameW||!this.w)return;
    let dy,dh;
    if(this.portrait){const scale=Math.min(this.w/this.frameW,this.h/this.frameH);dh=this.frameH*scale;dy=(this.h-dh)/2;}
    else if(this.narrow){dh=this.h*.52;dy=(this.h-dh)/2;}
    else{const scale=Math.max(this.w/this.frameW,this.h/this.frameH);dh=this.frameH*scale;dy=(this.h-dh)/2;}
    if(this.bandTop!==dy||this.bandBottom!==dy+dh){this.bandTop=dy;this.bandBottom=dy+dh;this.stage.style.setProperty('--iap-band-top',dy+'px');this.stage.style.setProperty('--iap-band-bottom',(dy+dh)+'px');}
  }
  update(drawNow=true){
    if(!this.owner.isConnected)return;
    const rect=this.root.getBoundingClientRect();const span=this.root.offsetHeight-this.h;
    const p=clamp(span>0?(this.owner.headerHeight-rect.top)/span:0);this.progress=p;
    const index=clamp(Math.round(p*(this.frames.length-1)),0,this.frames.length-1);this.index=index;this.frame=this.frames[index];
    if(!this.static&&this.near&&this.set){
      if(index!==this.requestedIndex||!this.set.active){const direction=this.requestedIndex==null||index>=this.requestedIndex?1:-1;this.requestedIndex=index;this.set.seek(index,direction);}
      if(drawNow)this.draw();
    }
  }
  scheduleDraw(){
    if(this.drawRaf)return;
    this.drawRaf=requestAnimationFrame(()=>{this.drawRaf=0;if(this.owner.isConnected)this.draw();});
  }
  draw(){
    if(!this.ctx||this.static||!this.set)return;
    let target=this.index||0;
    if(this.tau>0&&!this.owner.reduced){
      const now=performance.now(),dt=Math.min(100,now-(this.lastDraw||now));this.lastDraw=now;
      if(this.renderedIndex==null)this.shown=target;else this.shown+=(target-this.shown)*(1-Math.exp(-dt/this.tau));
      if(Math.abs(target-this.shown)<.5)this.shown=target;else this.scheduleDraw();
      target=Math.round(this.shown);
    }
    const ready=this.set.nearest(target);if(!ready)return;
    // Blend: while between two loaded frames, draw the lower one and the upper one on top
    // at the fractional weight, so slow scrolling dissolves instead of stepping.
    let blend=null;
    if(this.blend&&this.tau>0&&this.shown!==target&&ready.index===target){const lo=Math.floor(this.shown),hi=lo+1,a=this.set.images[lo],b=this.set.images[hi];if(a&&b&&hi<this.set.n)blend={a,b,t:this.shown-lo,key:lo+':'+Math.round((this.shown-lo)*16)};}
    const key=blend?blend.key:String(ready.index);
    if(this.renderedKey===key)return;this.renderedKey=key;
    const image=ready.image,displayFrame=this.frames[ready.index];
    this.renderedIndex=ready.index;
    if(this.frameW!==image.naturalWidth||this.frameH!==image.naturalHeight){this.frameW=image.naturalWidth;this.frameH=image.naturalHeight;this.canvas.width=this.frameW;this.canvas.height=this.frameH;this.band();}
    const t0=performance.now();
    if(blend){this.ctx.globalAlpha=1;this.ctx.drawImage(blend.a,0,0);this.ctx.globalAlpha=blend.t;this.ctx.drawImage(blend.b,0,0);this.ctx.globalAlpha=1;}
    else this.ctx.drawImage(image,0,0);
    (this.drawTimes||(this.drawTimes=[])).push(+(performance.now()-t0).toFixed(2));if(this.drawTimes.length>120)this.drawTimes.shift();
    if(!this.ready){this.ready=true;this.canvas.classList.add('iap-ready');if(this.poster)this.poster.hidden=true;}
    this.canvas.dataset.iapRenderedSource=image.src;this.canvas.dataset.iapRenderedFrame=String(displayFrame);
    if(this.readout===undefined)this.readout=this.owner.querySelector('[data-iap-frame-readout]');if(this.readout)this.readout.textContent='Frame '+String(displayFrame).padStart(4,'0');
    this.paint(displayFrame);
  }
  paint(f){
    const m=this.motion(f);
    this.caps.forEach(c=>{
      const [a,b]=c.in;const hasOut=c.out[1]>0;const [oa,ob]=hasOut?c.out:[Infinity,Infinity];
      const live=(!this.portrait||b<=this.end)&&f>=a-1&&f<=ob+1;c.el.style.visibility=live?'visible':'hidden';if(!live)return;
      const dy=0,gone=hasOut?ease(seg(f,oa,ob)):0;
      if(c.kind==='title'){
        c.el.style.transform=`translate(-50%, ${dy}px) scale(${1+.03*out(seg(f,this.start,44))})`;
        c.letters.forEach((letter,i)=>{const k=(i*7)%c.letters.length;letter.style.opacity=1-seg(f,oa+k/c.letters.length*6,oa+k/c.letters.length*6+5);});c.mark.style.opacity=1-seg(f,oa,oa+6);
      }else if(c.kind==='say'){
        c.words.forEach((word,i)=>word.style.transform=`translateY(${(1-out(seg(f,a+i*3.2,a+i*3.2+13)))*110}%)`);c.bar.style.width=out(seg(f,a+6,b+10))*100+'%';c.el.style.opacity=seg(f,a,a+4)*(1-gone);c.el.style.transform=`translateY(${dy-gone*26}px)`;
      }else{
        c.dot.style.transform=`scale(${out(seg(f,a,a+5))})`;c.line.style.width=out(seg(f,a+2,b+2))*60+'px';c.h.style.clipPath=`inset(0 ${100-ease(seg(f,a+1,b))*100}% 0 0)`;c.el.style.opacity=seg(f,a,a+4)*(1-gone);c.el.style.transform=`translateY(${dy-gone*18}px)`;
      }
    });
  }
  disconnect(){this.observer.disconnect();if(this.set)this.set.destroy();if(this.drawRaf)cancelAnimationFrame(this.drawRaf);}
}
window.IAPFrameSet=FrameSet;window.IAPStory=IAPStory;
})();
